/**
 * Scribe User Memory — SPA types.
 *
 * The BFF (`/api/memory/*`) flattens the auth memory store's generic envelope
 * (payload/context are opaque blobs there) back into this shape so the
 * SPA↔BFF contract matches the original transcriber_db-backed design —
 * `MemoryEntry` mirrors that flattened contract's snake_case field names as-is
 * rather than introducing a mapper for fields that never change shape between
 * backend and SPA. The one unavoidable change: `id` is now the auth-service
 * UUID string (Minni v2 moved the store off transcriber_db's autoincrement
 * ints), not a number.
 */

import { z } from 'zod';

export const MEMORY_CATEGORIES = ['style', 'structure', 'phrasing', 'terminology'] as const;
export type MemoryCategory = (typeof MEMORY_CATEGORIES)[number];

// `candidate` exists in the auth store but the BFF never returns it to the SPA
// (default status filter is `suggested,active`) — included here only so status
// comparisons stay exhaustive if a caller ever passes an explicit filter.
export const MEMORY_STATUSES = ['candidate', 'suggested', 'active', 'rejected', 'retired'] as const;
export type MemoryStatus = (typeof MEMORY_STATUSES)[number];

export const MEMORY_SOURCES = ['learned', 'manual', 'global'] as const;
export type MemorySource = (typeof MEMORY_SOURCES)[number];

/** Flattened auth memory-store entry, as the BFF returns it. */
export interface MemoryEntry {
  id: string;
  template_id: number | null;
  scope: string;
  source: MemorySource;
  category: MemoryCategory;
  instruction_text: string;
  status: MemoryStatus;
  confirm_only: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface MemoryEntriesResponse {
  entries: MemoryEntry[];
}

export interface MemoryEntryResponse {
  entry: MemoryEntry;
}

/** `POST /api/memory/entries` body. */
export interface CreateMemoryEntryPayload {
  category: MemoryCategory;
  instruction_text: string;
  template_id: number | null;
}

/** `PATCH /api/memory/entries/{id}` body — any subset of these fields. */
export interface UpdateMemoryEntryPayload {
  status?: 'active' | 'rejected' | 'retired';
  instruction_text?: string;
  template_id?: number | null;
}

export const MEMORY_INSTRUCTION_MAX_LENGTH = 500;

/** Category labels shown in the create form's Select (Icelandic, per repo UI language). */
export const MEMORY_CATEGORY_LABELS: Record<MemoryCategory, string> = {
  style: 'Stíll',
  structure: 'Uppbygging',
  phrasing: 'Orðalag',
  terminology: 'Fagorð',
};

export const createMemoryEntrySchema = z.object({
  category: z.enum(MEMORY_CATEGORIES, { error: 'Vinsamlegast veldu flokk.' }),
  instructionText: z
    .string()
    .trim()
    .min(1, { error: 'Leiðbeiningar mega ekki vera tómar.' })
    .max(MEMORY_INSTRUCTION_MAX_LENGTH, {
      error: `Leiðbeiningar mega ekki vera lengri en ${MEMORY_INSTRUCTION_MAX_LENGTH} stafir.`,
    }),
  templateId: z.number().int().positive().nullable(),
});

export type CreateMemoryEntryFormValues = z.infer<typeof createMemoryEntrySchema>;
